import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'

/* ------------------------------------------------------------------
   کمبوباکس جست‌وجوپذیر

   ==================================================================
   🔴 فهرست در **Portal** رندر می‌شود، نه کنار ورودی.

   ⚠️ دو خرابیِ واقعی که همین یک تصمیم هر دو را می‌بندد:

     ۱. `Dialog` بدنه‌اش `overflow-y-auto` دارد. فهرستی که با
        `position: absolute` کنار ورودی بنشیند، از ظرفِ اسکرول‌دار
        **بریده** می‌شود — کاربر فهرست را نمی‌بیند و فکر می‌کند
        خالی است.

     ۲. حتی اگر بریده نشود، `z-index` داخل مدال نسبت به همان
        stacking context حساب می‌شود و زیر پردهٔ مدال می‌ماند.

   🔴 هر دو در آزمون مرورگر دیده شدند، نه در تست: تستِ DOM گره را
   پیدا می‌کند چون **هست** — فقط دیده نمی‌شود.

   ==================================================================
   ⚠️ حالت چندانتخابی (`multiple`) متن جست‌وجو را پس از انتخاب پاک
   می‌کند.

   🔴 بدون آن، انتخابِ نفر دوم ناممکن بود: پس از انتخاب نفر اول،
   متنِ جست‌وجو به نامِ او تبدیل می‌شد، فهرست به همان یک نفر فیلتر
   می‌شد و او هم «انتخاب‌شده» بود — یعنی فهرستی که فقط یک گزینهٔ
   غیرفعال داشت.
   ------------------------------------------------------------------ */

export interface SearchSelectOption {
  /** مقدارِ برگشتی — همان چیزی که به سرور می‌رود */
  value: string
  label: string
  /** خط دوم — واحد سازمانی، کد پرسنلی و مانند آن */
  hint?: ReactNode
  disabled?: boolean
  disabledReason?: string
}

export interface SearchSelectProps {
  options: SearchSelectOption[]
  value: string
  onChange: (value: string, option: SearchSelectOption | null) => void
  /** متن جست‌وجو — بیرون نگه داشته می‌شود تا صدا زدن API ممکن باشد */
  search: string
  onSearchChange: (search: string) => void
  /**
   * چندانتخابی: پس از هر انتخاب، متن پاک و فهرست باز می‌ماند.
   *
   * ⚠️ خودِ چیپ‌ها را صداکننده رندر می‌کند؛ این مؤلفه فقط انتخاب را
   * ممکن می‌کند.
   */
  multiple?: boolean
  placeholder?: string
  emptyLabel?: string
  loading?: boolean
  disabled?: boolean
  id?: string
  className?: string
  /** بیشینهٔ ارتفاع فهرست بازشده */
  maxHeight?: number
  /**
   * لنگرِ فوکوس — روی خودِ ورودی می‌نشیند تا `focusFieldByAnchor`
   * بتواند پس از خطا کاربر را همین‌جا بیاورد.
   *
   * ⚠️ عمداً روی `<input>` داخلی است و نه روی پوششِ بیرونی: آن پوشش
   * فوکوس‌پذیر نیست و کمکِ فوکوس مجبور می‌شد حدس بزند کدام گرهٔ داخلی
   * را بردارد.
   */
  'data-field'?: string
  /**
   * اقدامِ کوچکِ داخلِ ورودی — در انتهای فیلد (چپ در RTL) می‌نشیند.
   *
   * ⚠️ مثلاً «افزودنِ گزینه‌ای که در فهرست نیست»: جایی که کاربر تازه
   * می‌فهمد نبودنش را، همین‌جاست و نه در دکمه‌ای آن‌سوی فرم.
   *
   * 🔴 برخلافِ نشانهٔ ✓، این ناحیه کلیک‌پذیر است؛ پس ورودی از همان
   * سمت `padding` می‌گیرد تا متنِ جست‌وجو زیرش نرود.
   */
  trailing?: ReactNode
}

export function SearchSelect({
  options,
  value,
  onChange,
  search,
  onSearchChange,
  multiple = false,
  placeholder = 'برای جست‌وجو بنویسید…',
  emptyLabel = 'موردی یافت نشد.',
  loading = false,
  disabled = false,
  id,
  className,
  maxHeight = 240,
  'data-field': dataField,
  trailing,
}: SearchSelectProps) {
  const generatedId = useId()
  const listId = `${id ?? generatedId}-listbox`

  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null)

  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  )

  /**
   * مختصات فهرست — نسبت به viewport، چون `position: fixed` است.
   *
   * ⚠️ اگر جا نبود، بالای ورودی باز می‌شود. کمبوباکسی که پایین صفحه
   * است وگرنه فهرستش بیرون از دید می‌افتد.
   */
  const place = useCallback(() => {
    const anchor = inputRef.current

    if (!anchor) return

    const box = anchor.getBoundingClientRect()
    const below = window.innerHeight - box.bottom
    const openUp = below < Math.min(maxHeight, 160) && box.top > below

    setRect({
      top: openUp ? box.top - Math.min(maxHeight, box.top - 8) - 4 : box.bottom + 4,
      left: box.left,
      width: box.width,
    })
  }, [maxHeight])

  useLayoutEffect(() => {
    if (open) place()
  }, [open, place, options.length])

  /*
    ⚠️ اسکرول و تغییر اندازه، فهرست را جابه‌جا می‌کنند.

    🔴 `position: fixed` با صفحه اسکرول نمی‌شود؛ بدون این شنونده،
    فهرست سرِ جای قبلی‌اش می‌ماند و از ورودی جدا می‌افتد.
  */
  useEffect(() => {
    if (!open) return

    const onScrollOrResize = () => place()

    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [open, place])

  /* کلیک بیرون می‌بندد — فهرست در Portal است، پس هر دو ناحیه بررسی می‌شوند. */
  useEffect(() => {
    if (!open) return

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node

      if (rootRef.current?.contains(target) || listRef.current?.contains(target)) return

      setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)

    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  useEffect(() => {
    setActive(0)
  }, [search, options.length])

  const commit = (option: SearchSelectOption) => {
    if (option.disabled) return

    onChange(option.value, option)

    if (multiple) {
      /*
        🔴 متن پاک می‌شود و فهرست باز می‌ماند — تا نفر بعدی هم
        انتخاب شود.
      */
      onSearchChange('')
      inputRef.current?.focus()

      return
    }

    onSearchChange(option.label)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()

      if (!open) {
        setOpen(true)

        return
      }

      const step = e.key === 'ArrowDown' ? 1 : -1

      setActive((prev) => {
        const next = prev + step

        if (next < 0) return Math.max(0, options.length - 1)
        if (next >= options.length) return 0

        return next
      })

      return
    }

    if (e.key === 'Enter' && open && options[active]) {
      e.preventDefault()
      commit(options[active])

      return
    }

    if (e.key === 'Escape' && open) {
      e.preventDefault()
      setOpen(false)
    }
  }

  const list =
    open && rect !== null
      ? createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            style={{
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              width: rect.width,
              maxHeight,
              zIndex: 'var(--pf-z-popover)',
            }}
            className="overflow-y-auto rounded-[var(--pf-radius-md)] border border-[var(--pf-border)] bg-[var(--pf-surface)] shadow-[var(--pf-shadow-lg)]"
          >
            {loading ? (
              <li className="px-[var(--pf-space-3)] py-[var(--pf-space-2)] text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">
                در حال جست‌وجو…
              </li>
            ) : options.length === 0 ? (
              <li className="px-[var(--pf-space-3)] py-[var(--pf-space-2)] text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">
                {emptyLabel}
              </li>
            ) : (
              options.map((option, index) => (
                <li key={option.value} role="option" aria-selected={option.value === value}>
                  <button
                    type="button"
                    disabled={option.disabled}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => commit(option)}
                    className={cn(
                      'flex w-full items-center justify-between gap-[var(--pf-space-2)] px-[var(--pf-space-3)] py-[var(--pf-space-2)] text-start',
                      index === active && 'bg-[var(--pf-surface-hover)]',
                      option.disabled && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <span className="text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
                      {option.disabled && option.disabledReason
                        ? option.disabledReason
                        : option.hint}
                    </span>
                    <span>{option.label}</span>
                  </button>
                </li>
              ))
            )}
          </ul>,
          document.body,
        )
      : null

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          data-field={dataField}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={search}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onChange={(e) => {
            onSearchChange(e.target.value)
            setOpen(true)

            /*
              🔴 دست‌کاریِ متن، انتخاب قبلی را باطل می‌کند.

              ⚠️ وگرنه کاربر نامِ نشسته را پاک می‌کرد، اسم دیگری
              می‌نوشت، و بی‌آنکه بداند همان انتخابِ اول ارسال می‌شد.

              ⚠️ در حالت چندانتخابی این کار لازم نیست: `value` آنجا
              همیشه خالی است و انتخاب‌ها را صداکننده نگه می‌دارد.
            */
            if (! multiple && value !== '') onChange('', null)
          }}
          /*
            ⚠️ همان مدل «Filled» بقیهٔ کنترل‌ها.

            پیش‌تر این ورودی از سه جهت با بقیه فرق داشت: گردیِ
            `radius-md` به‌جای `radius`، حاشیهٔ `--pf-border` به‌جای
            `border-strong`، و مهم‌تر از همه فوکوسِ **آبی**
            (`--pf-brand-500`) در حالی که رنگِ فعالِ سامانه سبز است.
            یعنی یک فیلد در فرم، هم شکلش و هم رنگِ حالت فعالش با
            بقیه یکی نبود.
          */
          className={cn(
            'w-full rounded-t-[var(--pf-radius)] rounded-b-[var(--pf-radius-sm)]',
            /* ⚠️ سه طرف صریح شفاف؛ خطِ زیرین ۲px و به رنگِ متنِ کم‌رنگ —
               ۱px با `border-strong` روی زمینهٔ فیلد دیده نمی‌شد. */
            'border border-x-transparent border-t-transparent',
            'border-b-2 border-b-[var(--pf-text-subtle)]',
            'bg-[var(--pf-surface-muted)] py-[var(--pf-space-2)] outline-none',
            'transition-[background-color,border-color] duration-[var(--pf-duration-fast)]',
            'placeholder:text-[var(--pf-text-subtle)]',
            !disabled && 'hover:bg-[var(--pf-surface-hover)]',
            'focus:border-b-[var(--pf-primary-container)]',
            disabled && 'cursor-not-allowed opacity-60',
            /*
              ==================================================================
              🔴 `padding` افقی به‌صورت دو کلاسِ جداست و نه `px-*`.

              ⚠️ `cn` یک به‌هم‌چسبانِ رشته است و نه tailwind-merge. اگر
              `px-3` می‌ماند و `pl-10` هم اضافه می‌شد، هر دو با
              specificity برابر روی عنصر می‌نشستند و برنده ترتیبِ
              خروجیِ Tailwind بود — همان دامی که در `Button` برای
              `iconOnly` مستندش کرده‌ایم.

              ⚠️ پهنای انتها به آن‌چه واقعاً داخل ورودی نشسته بستگی
              دارد: جا دادن به دکمه‌ای که وجود ندارد، متنِ جست‌وجوی
              هشت فیلدِ دیگرِ سامانه را بی‌دلیل از لبه دور می‌کرد.
              ==================================================================
            */
            'pr-[var(--pf-space-3)]',
            trailing != null ? 'pl-[var(--pf-space-10)]' : 'pl-[var(--pf-space-3)]',
          )}
        />

        {/*
          🔴 نشانهٔ ✓ و اقدامِ داخلِ ورودی در **یک ردیف** می‌نشینند.

          ⚠️ اگر هر کدام جدا `absolute` می‌شدند، با انتخابِ یک گزینه
          روی هم می‌افتادند — چون هر دو انتهای ورودی را می‌خواهند.
        */}
        <div className="absolute inset-y-0 left-[var(--pf-space-2)] flex items-center gap-[var(--pf-space-1)]">
          {selected !== null ? (
            <span aria-hidden className="text-[var(--pf-success-600)]">
              ✓
            </span>
          ) : null}

          {trailing != null ? (
            /*
              🔴 کلیک روی اقدام، فهرست را می‌بندد.

              ⚠️ این ناحیه داخلِ `rootRef` است، پس شنوندهٔ «کلیکِ
              بیرون» آن را بیرون حساب نمی‌کند و فهرست باز می‌ماند.
              اگر اقدام یک دیالوگ باز کند، فهرستِ Portal‌شده **روی**
              آن می‌نشیند — چون `--pf-z-popover` است.
            */
            <div onPointerDown={() => setOpen(false)}>{trailing}</div>
          ) : null}
        </div>
      </div>

      {list}
    </div>
  )
}
